import type React from 'react';
import { BaseDatePicker, BaseSelect } from '@components';
import { BORDER_COLORS, DEFAULT_COLORS, SURFACE_COLORS, TEXT_COLORS } from '@constants/colors.ts';
import { BODY_MD_MD_STYLES, BODY_SM_SM_MEDIUM_STYLES, BODY_SM_SM_STYLES } from '@constants/text.ts';
import type { CommonTableStyledProps } from '@routes/sidebar/admin-center/___shared___/components/tables/types.ts';
import { antdTableFilterStyles } from '@routes/sidebar/admin-center/___shared___/styled.ts';
import type { TableProps } from 'antd';
import { Table } from 'antd';
import type { AnyObject } from 'antd/es/_util/type';
import styled, { css } from 'styled-components';

const RawStyledTable = styled(Table)<CommonTableStyledProps>`
    ${({ $noData, $noRecordsFound, $pageSizerLabel, tableLayout }) => css`
        /* Scroll containers */
        & :where(.ant-table-container) .ant-table-body,
        & :where(.ant-table-container) .ant-table-content {
            scrollbar-width: thin;
            scrollbar-color: var(--colors-surface-action, #2171c6) var(--colors-border-primary, #e2e8f0);
            scrollbar-gutter: auto;

            &::-webkit-scrollbar {
                height: 8px;
                width: 8px;
            }

            &::-webkit-scrollbar-thumb {
                background-color: var(--colors-surface-action, #2171c6);
                border-radius: 8px;
            }

            &::-webkit-scrollbar-track {
                background: var(--colors-border-primary, #e2e8f0);
            }
            border-right: 1px solid #f0f0f0;
        }

        /* HEADER styles — body/sm/sm-medium */
        & :where(.ant-table) .ant-table-thead > tr > th {
            background: var(--colors-grey-150, #f1f4f8);
            color: var(--colors-text-body, #50566e);
            border-bottom: 1px solid var(--colors-border-primary, #e2e8f0);
            ${BODY_SM_SM_MEDIUM_STYLES};
        }

        & :where(.ant-table) .ant-table-sticky-holder {
            background: var(--colors-grey-150, #f1f4f8);
            border-bottom: 1px solid var(--colors-border-primary, #e2e8f0);
        }

        & :where(.ant-table).ant-table-bordered .ant-table-thead > tr > th {
            border-color: var(--colors-border-primary, #e2e8f0);
        }

        ${($noData || $noRecordsFound) &&
        css`
            & :where(.ant-table) .ant-table-thead > tr > th:first-child {
                border-bottom-left-radius: 8px !important;
            }

            & :where(.ant-table) .ant-table-thead > tr > th:last-child {
                border-bottom-right-radius: 8px !important;
            }

            /* don’t allow horizontal scrolling in empty state */
            & :where(.ant-table-container) .ant-table-body,
            & :where(.ant-table-container) .ant-table-content {
                overflow-x: hidden !important;
            }

            /* looks like AntD miscalculates when layout fixed so this should not be applied */
            ${tableLayout !== 'fixed' &&
            css`
                & .ant-table-expanded-row-fixed {
                    width: 100% !important;
                }
            `}
        `}

        /* Remove vertical dividers for ALL cells when bordered (stable selector, no dev class) */
        &
        .ant-table.ant-table-bordered
        > .ant-table-container
        > :is(.ant-table-content, .ant-table-header, .ant-table-body, .ant-table-summary)
        > table
        > :is(thead, tbody, tfoot)
        > tr
        > :is(th, td) {
            border-inline-end: 0;
            border-right: 0;
        }

        /* Hide AntD header hairline pseudo-divider */
        & :where(.ant-table) .ant-table-thead > tr > th::before,
        & :where(.ant-table) .ant-table-thead > tr > td::before {
            content: none !important;
            width: 0;
            background: none;
        }

        /* Add strong separators only at fixed column boundaries */
        & .ant-table .ant-table-container .ant-table-cell-fix-left-last {
            border-inline-end: 3px solid var(--colors-border-primary, #e2e8f0) !important;
            border-right: 3px solid var(--colors-border-primary, #e2e8f0) !important;
        }

        & .ant-table .ant-table-container .ant-table-cell-fix-right-first {
            border-inline-start: 3px solid var(--colors-border-primary, #e2e8f0) !important;
            border-left: 3px solid var(--colors-border-primary, #e2e8f0) !important;
        }

        /* Hide AntD gradient shadows so our borders stay crisp */
        & .ant-table .ant-table-container .ant-table-cell-fix-left-last::after,
        & .ant-table .ant-table-container .ant-table-cell-fix-right-first::after {
            box-shadow: none;
            opacity: 0;
        }

        /* === BODY CELLS: unified text + bottom border on every row === */
        & :where(.ant-table) .ant-table-tbody > tr > td {
            color: var(--colors-text-body, #50566e) !important;
            ${BODY_SM_SM_STYLES};

            border-bottom: 1px solid var(--colors-border-primary, #e2e8f0);
            background: ${DEFAULT_COLORS.white};
        }

        & :where(.ant-table) .ant-table-tbody > tr:nth-child(odd) > td {
            background: var(--neutral-100, #f9fafb);
        }

        /* Optional: explicit active/inactive row overrides */
        & :where(.ant-table) .ant-table-tbody > tr.row-active > td {
            color: var(--colors-text-body, #50566e);
        }
        & :where(.ant-table) .ant-table-tbody > tr.row-inactive > td {
            color: var(--colors-text-disabled, #b4b8bf);
        }

        /* Pagination */
        && .ant-table-pagination.ant-pagination {
            display: flex;
            align-items: center;
            flex-wrap: nowrap;
            margin: 24px 0;
            padding: 8px 0;
        }

        && .ant-table-pagination.ant-pagination li {
            border-radius: var(--border-radius-s_8, 8px);
        }

        && .ant-table-pagination .ant-pagination-prev,
        && .ant-table-pagination .ant-pagination-next,
        && .ant-table-pagination .ant-pagination-item > a,
        && .ant-table-pagination .ant-pagination-item-link {
            margin: 0 4px;
            color: ${TEXT_COLORS.action};
            ${BODY_MD_MD_STYLES};
        }

        && .ant-table-pagination .ant-pagination-item-active {
            border-radius: 8px;
            border: 1px solid ${TEXT_COLORS.action};
            background: ${SURFACE_COLORS.page};
        }

        && .ant-table-pagination .ant-pagination-item-active > a {
            color: ${TEXT_COLORS.action};
            ${BODY_MD_MD_STYLES};
        }

        /* right-aligned options + label */
        && .ant-table-pagination .ant-pagination-options {
            margin-left: auto;
            display: flex;
            align-items: center;
        }

        /* label before the size select */
        && .ant-table-pagination .ant-pagination-options::before {
            content: '${$pageSizerLabel ?? 'Clients per page'}';
            margin-right: 8px;
            white-space: nowrap;
            color: ${TEXT_COLORS.body};
            ${BODY_SM_SM_STYLES};
        }

        && .ant-table-pagination .ant-select-selector {
            border-radius: 8px;
            border: 1px solid ${TEXT_COLORS.disabled};
            background: ${DEFAULT_COLORS.white};
        }

        /* rows: inactive */
        & :where(.ant-table) .ant-table-tbody > tr.row-inactive > td {
            color: var(--colors-text-disabled, #b4b8bf) !important;
            ${BODY_SM_SM_STYLES};
        }

        /* hide antd's default sort arrows */
        & :where(.ant-table) .ant-table-column-sorters .ant-table-column-sorter {
            display: none !important;
        }

        && .ant-table-pagination .ant-pagination-item-container {
            top: -12px;
        }

        // for dropdown list during edit in ant select to make all elements visible
        & span.ant-select-selection-wrap {
            z-index: 1;
        }

        ${antdTableFilterStyles};
    `}
`;

export const StyledTable = RawStyledTable as unknown as <T extends AnyObject = AnyObject>(
    props: TableProps<T> & CommonTableStyledProps,
) => React.JSX.Element;

export const ActionsTableWrapper = styled.div<{ $isEdit?: boolean }>`
    height: 23px;

    ${({ $isEdit }) =>
        $isEdit &&
        `
            display: flex;
            height: 23px;
            justify-content: center;
            gap: 8px;
        `};
`;

export const StyledSelect = styled(BaseSelect)`
    && .ant-select-selector {
        position: relative;
    }

    && .ant-select-selection-placeholder {
        position: absolute;
        inset-inline: 0;
        left: 50%;
        right: auto;
        transform: translateX(-50%);
        width: max-content;
        text-align: center;
        pointer-events: none;

        color: var(--colors-text-on-disabled, #70768d);
        font-family: var(--type-font-family, Poppins), serif;
        font-size: var(--fontsize-body-sm, 14px);
        font-style: normal;
        font-weight: 400;
        line-height: var(--line-height-body-sm, 20px);
    }

    &&.ant-select-single .ant-select-selector .ant-select-selection-placeholder {
        left: 50%;
        right: auto;
        transform: translateX(-50%);
    }

    &&.ant-select-status-error .ant-select-selector {
        border: 2px solid ${BORDER_COLORS.error} !important;
    }
`;

export const NoFilterText = styled.div`
    color: var(--colors-text-body, #50566e);
    text-align: center;
    ${BODY_SM_SM_MEDIUM_STYLES};
`;

export const FilterEmptyTextWrapper = styled.div`
    display: flex;
    padding: var(--spacing-md, 24px) 0;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-2xs, 4px);

    svg {
        width: 81px;
        height: 66px;
    }
`;

export const StyledDatePicker = styled(BaseDatePicker)`
    &&.ant-picker-status-error {
        border: 2px solid ${BORDER_COLORS.error} !important;
    }
`;
